import type { RouteRecordRaw } from 'vue-router'
import PassThrough from '@/components/layout/PassThrough.vue'
import {
  userAccountsDataRetentionRoute,
  userAccountsMyDataRoute,
  userAccountsSubAccountsRoute,
  userAccountsUsersRoute,
} from '@/routes/user/accounts/workspace'
import {
  userAccountsBillingOverviewRoute,
  userAccountsInvoicesRoute,
  userAccountsMonthlyPlansRoute,
  userAccountsStatementsRoute,
} from '@/routes/user/accounts/billing'
import {
  userAccountsDetailsRoute,
  userAccountsNotificationsRoute,
  userAccountsSecurityRoute,
} from '@/routes/user/accounts/personal'
import { UserSettingsRoutes } from '@/routes/user/settings'
import { getOldRouteWithRedirect } from '@/utils/oldRouteRedirect'

export const userAccountsSettingsRoute: RouteRecordRaw = {
  name: 'user.accounts.settings',
  path: 'settings',
  meta: {
    permission: 'accountSettings',
  },
  component: PassThrough,
  redirect: { name: 'user.accounts.settings.profile' },
  children: [
    {
      name: 'user.accounts.settings.profile',
      path: 'profile',
      redirect: { name: UserSettingsRoutes.AccountDetails },
    },
    {
      name: 'user.accounts.settings.sso',
      path: 'sso',
      redirect: { name: UserSettingsRoutes.WorkspaceSso },
    },
    {
      name: 'user.accounts.settings.security',
      path: 'security',
      redirect: { name: 'user.accounts.security' },
    },
    {
      name: 'user.accounts.settings.closeAccount',
      path: 'close',
      redirect: { name: UserSettingsRoutes.AccountDetails },
    },
  ],
}

export const userAccountsSsoRoute: RouteRecordRaw = getOldRouteWithRedirect(
  'user.accounts.sso',
  'sso',
  UserSettingsRoutes.WorkspaceSso,
)

export const getAccountsRoutes = (): RouteRecordRaw[] => [
  getOldRouteWithRedirect(
    'user.accounts.subAccounts.import.flow',
    'sub-accounts/import/flow',
    UserSettingsRoutes.WorkspaceSubaccountsImport,
  ),
  {
    name: 'user.accounts.subAccountDetails',
    path: 'sub-accounts/:subAccountId',
    redirect: { name: UserSettingsRoutes.WorkspaceSubaccountsDetails },
    children: [
      getOldRouteWithRedirect(
        'user.accounts.subAccountDetails.overview',
        '',
        UserSettingsRoutes.WorkspaceSubaccountsDetailsOverview,
      ),
      getOldRouteWithRedirect(
        'user.accounts.subAccountDetails.permissions',
        'permissions',
        UserSettingsRoutes.WorkspaceSubaccountsDetailsPermissions,
      ),
      getOldRouteWithRedirect(
        'user.accounts.subAccountDetails.sent',
        'sent',
        UserSettingsRoutes.WorkspaceSubaccountsDetailsSent,
      ),
      getOldRouteWithRedirect(
        'user.accounts.subAccountDetails.received',
        'received',
        UserSettingsRoutes.WorkspaceSubaccountsDetailsReceived,
      ),
    ],
  },
  {
    name: 'user.accounts.userDetails',
    path: 'users/:subAccountId',
    redirect: { name: 'user.accounts.userDetails.overview' }, // UserSettingsRoutes.WorkspaceUsersDetails
    children: [
      getOldRouteWithRedirect(
        'user.accounts.userDetails.overview',
        '',
        UserSettingsRoutes.WorkspaceUsersDetailsOverview,
      ),
      getOldRouteWithRedirect(
        'user.accounts.userDetails.permissions',
        'permissions',
        UserSettingsRoutes.WorkspaceUsersDetailsPermissions,
      ),
    ],
  },
  {
    name: 'user.accounts.subAccounts.import.details',
    path: 'sub-accounts/import/:subAccountImportId',
    redirect: { name: 'user.accounts.subAccounts.import.details.invited' },
    children: [
      getOldRouteWithRedirect(
        'user.accounts.subAccounts.import.details.invited',
        'invited',
        UserSettingsRoutes.WorkspaceSubaccountsImportDetailsInvited,
      ),
      getOldRouteWithRedirect(
        'user.accounts.subAccounts.import.details.duplicated',
        'duplicated',
        UserSettingsRoutes.WorkspaceSubaccountsImportDetailsDuplicated,
      ),
      getOldRouteWithRedirect(
        'user.accounts.subAccounts.import.details.failed',
        'failed',
        UserSettingsRoutes.WorkspaceSubaccountsImportDetailsFailed,
      ),
    ],
  },
]

export const oldUserAccountsRoute: RouteRecordRaw = {
  name: 'user.accounts',
  path: 'accounts',
  component: PassThrough,
  children: [
    userAccountsSubAccountsRoute,
    userAccountsUsersRoute,
    userAccountsDataRetentionRoute,
    userAccountsMyDataRoute,
    userAccountsBillingOverviewRoute,
    userAccountsMonthlyPlansRoute,
    userAccountsInvoicesRoute,
    userAccountsStatementsRoute,
    userAccountsDetailsRoute,
    userAccountsSecurityRoute,
    userAccountsNotificationsRoute,
    userAccountsSettingsRoute,
    userAccountsSsoRoute,
    ...getAccountsRoutes(),
  ],
}
